import unLinked from 'assets/image/auth-steps/unLinked.png'
import clsx from 'clsx'
import { AuthTemplate } from 'components/templates/authTemplate'
import { typo } from 'shared/styles'
import { LoadingButton, StackIcon } from 'shared/ui'
import { ControlledDialog } from 'shared/ui/Dialog'

import { LinkedInLoginPhase } from './constants'
import { LinkedInLoginV2 } from './LinkedInLoginV2'
import css from './LinkedInLoginModal.module.scss'
import type { useLinkedInPasswordLogin } from './useLinkedInPasswordLogin'

type LoginState = ReturnType<typeof useLinkedInPasswordLogin>

const cn = AuthTemplate.cn

type LinkedAccountHint = {
	displayName: string
	href: string
}

type LinkedInLoginModalProps = {
	description?: string
	linkedAccount?: LinkedAccountHint | null
	login: LoginState
	showImage?: boolean
	title?: string
}

function LinkedInLoginModal({
	description,
	linkedAccount,
	login,
	showImage = true,
	title
}: LinkedInLoginModalProps) {
	if (!login.open) {
		return null
	}

	const busy = login.phase === LinkedInLoginPhase.Submitting
	const isDone = login.phase === LinkedInLoginPhase.Done
	const heading = isDone
		? 'Congratulations, you are done!'
		: (title ?? 'Link your LinkedIn account')

	const handleClose = () => {
		if (!busy) {
			login.close()
		}
	}

	const result = (
		<ControlledDialog
			className={css.modalInner}
			closeOnBackdrop={isDone}
			onRequestClose={handleClose}
			open={login.open}
			rootClassName={`${css.dialog} app-scroll`}
		>
			<button
				aria-label='Close.'
				className={css.close}
				disabled={busy}
				onClick={handleClose}
				title='Close.'
				type='button'
			>
				<StackIcon icon='close' stroke='currentcolor' width={9} />
			</button>

			<div className={css.bodyModal}>
				<h1 className={clsx(cn.title, css.title)}>{heading}</h1>

				{showImage && isDone && (
					<img
						alt='LinkedIn account'
						className={clsx(cn.image, css.image)}
						height={130}
						src={unLinked}
						width={130}
					/>
				)}

				{renderLoginHint(description, linkedAccount, isDone)}

				{!isDone && login.error && <p className={css.error}>{login.error}</p>}

				{!isDone && (
					<LinkedInLoginV2
						onFailed={message => login.setError(message)}
						onSolved={solved => {
							void login.finishCaptcha(
								solved.headers,
								solved.profile,
								solved.correlationId
							)
						}}
					/>
				)}

				{isDone && (
					<div className={css.actions}>
						<LoadingButton
							className={clsx(cn.action, css.action)}
							onClick={handleClose}
							type='button'
							typography={typo.largeButton}
						>
							Continue
						</LoadingButton>
					</div>
				)}
			</div>
		</ControlledDialog>
	)
	return result
}

function renderLoginHint(
	description: string | undefined,
	linkedAccount: LinkedAccountHint | null | undefined,
	isDone: boolean
) {
	if (isDone) {
		return null
	}

	if (linkedAccount) {
		const result = (
			<p className={clsx(cn.text, css.text)}>
				Sign in to LinkedIn with account{' '}
				<a
					className={css.linkedAccount}
					href={linkedAccount.href}
					rel='noopener noreferrer'
					target='_blank'
				>
					{linkedAccount.displayName}
				</a>
			</p>
		)
		return result
	}

	if (!description) {
		return null
	}

	const result = <p className={clsx(cn.text, css.text)}>{description}</p>
	return result
}

export { LinkedInLoginModal }
export type { LinkedAccountHint }
